import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Chip, Radio, RadioGroup, FormControlLabel,
  CircularProgress, Snackbar, Alert,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Select, FormControl, InputLabel,
  Stack, useTheme
} from '@mui/material';
import PageHeader from '../components/headers/Header';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getGoalStatsByPeriod, getGoalStatsByContext, getGoals, createGoal, updateGoal, deleteGoal } from '../services/goalAPIs';
import { getOffices } from '../services/officeAPIs';
import { getAllEmployees } from '../services/EmployeeService';
import { getEmployeeImage } from '../services/HRstaff';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const colors = {
  primary1: '#3d52a0',
  primary2: '#7091e6',
  primary3: '#8697c4',
  primary4: '#adbbda',
  primary5: '#ede8f5',
  success: '#4caf50',
  successLight: '#e8f5e8',
  warning: '#ff9800',
  warningLight: '#fff3e0',
  danger: '#f44336',
  dangerLight: '#ffebee',
  info: '#2196f3',
  infoLight: '#e3f2fd',
  safe: '#00c853',
  safeLight: '#e8f8f5',
  lightBlue: '#e3f2fd',
  lightBlueDark: '#1976d2'
};

// Styled components
const ActionButton = styled(Button)(({ theme }) => ({
  background: colors.primary2,
  color: 'white',
  fontWeight: 'bold',
  '&:hover': {
    background: colors.primary1
  }
}));

const EditableTableCell = styled(TableCell)(({ theme, isEditing }) => ({
  position: 'relative',
  cursor: isEditing ? 'text' : 'pointer',
  backgroundColor: isEditing ? colors.warningLight : 'transparent',
  '&:hover': {
    backgroundColor: isEditing ? colors.warningLight : colors.primary5,
  },
  transition: 'background-color 0.2s ease'
}));

const ClickableTableCell = styled(TableCell)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: colors.infoLight,
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[2],
    borderRadius: '8px',
  },
  '&:active': {
    transform: 'scale(0.98)',
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: theme.shadows[6],
  overflow: 'hidden',
  border: `2px solid ${colors.primary2}`,
  '& .MuiTableHead-root': {
    backgroundColor: colors.primary1,
    '& .MuiTableCell-head': {
      color: 'white',
      fontWeight: 'bold',
      fontSize: '1.1rem',
      padding: theme.spacing(2)
    }
  },
  '& .MuiTableRow-root:nth-of-type(even)': {
    backgroundColor: colors.primary5,
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: colors.infoLight,
  },
  '& .MuiTableCell-root': {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${colors.primary3}`
  }
}));

const positions = ['HR Manager', 'Project Manager', 'Customer Service', 'HR', 'Manager', 'Account Manager', 'Quality Reviewer', 'Bookkeeper', 'Planner', 'Personal Assistance', 'Software Engineer', 'Client Adviser', 'IT Professional'];

const GoalManagement = () => {
  const theme = useTheme();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffice, setSelectedOffice] = useState('');
  const [offices, setOffices] = useState([]);

  // Data Stores
  const [employeeCountByOffice, setEmployeeCountByOffice] = useState({});
  const [employeeCountByPositionPerOffice, setEmployeeCountByPositionPerOffice] = useState({});
  const [employeeFteByPositionPerOffice, setEmployeeFteByPositionPerOffice] = useState({});

  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editFteValue, setEditFteValue] = useState('');
  const [editFteMode, setEditFteMode] = useState('perPerson'); // 'perPerson' or 'total'

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [employeeOverlay, setEmployeeOverlay] = useState({
    open: false,
    position: '',
    employees: [],
    loading: false,
    searchTerm: ''
  });

  const [profileImages, setProfileImages] = useState({});

  const [formData, setFormData] = useState({
    goal: '',
    date: '',
    context: '',
    position: '',
    description: '',
    scale: 1,
    fte: 1,
    location: ''
  });

  // --- Initial Load ---
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const officeFromUrl = searchParams.get('office');
    if (officeFromUrl && offices.length > 0) {
      setSelectedOffice(officeFromUrl);
    } else if (offices.length > 0) {
      setSelectedOffice('All Offices');
    }
  }, [offices, searchParams]);

  // --- Data Processing (Backend FTE Logic) ---
  const processEmployeeData = (employeesList) => {
    const counts = {}; // { OfficeName: { PositionName: Count } }
    const ftes = {};   // { OfficeName: { PositionName: TotalFTE } }
    const officeTotals = {}; // { OfficeName: TotalCount }

    employeesList.forEach(emp => {
      const officeName = emp.office?.name;
      if (!officeName) return;

      // Init office entries if missing
      if (!counts[officeName]) counts[officeName] = {};
      if (!ftes[officeName]) ftes[officeName] = {};
      if (!officeTotals[officeName]) officeTotals[officeName] = 0;

      officeTotals[officeName] += 1;

      // Check new 'positions' array from backend (EmployeePosition Entity)
      if (emp.positions && Array.isArray(emp.positions) && emp.positions.length > 0) {
        // Aggregate FTE based on specific positions
        emp.positions.forEach(pos => {
          const pName = pos.positionName;
          const pFte = parseFloat(pos.fte) || 1.0;
          counts[officeName][pName] = (counts[officeName][pName] || 0) + 1;
          ftes[officeName][pName] = (ftes[officeName][pName] || 0) + pFte;
        });
      } else if (emp.position) {
        // Fallback for legacy single position data (Assumes FTE 1.0)
        const pName = emp.position;
        counts[officeName][pName] = (counts[officeName][pName] || 0) + 1;
        ftes[officeName][pName] = (ftes[officeName][pName] || 0) + 1.0;
      }
    });

    setEmployeeCountByOffice(officeTotals);
    setEmployeeCountByPositionPerOffice(counts);
    setEmployeeFteByPositionPerOffice(ftes);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [goalsData, officesData, allEmployees] = await Promise.all([
        getGoals(),
        getOffices(),
        getAllEmployees()
      ]);

      setGoals(goalsData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      setOffices(officesData);
      processEmployeeData(allEmployees); // Process FTE locally from fetched data
      setError(null);
    } catch (error) {
      console.error('Failed to load ', error);
      setError('Failed to load goals. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // --- Image Fetching ---
  const fetchSingleImage = useCallback(async (employeeId) => {
    try {
      if (!employeeId || profileImages[employeeId]) return null;
      const imageBlob = await getEmployeeImage(employeeId);
      if (imageBlob) return { employeeId, imageUrl: imageBlob };
      return null;
    } catch (error) {
      return null;
    }
  }, [profileImages]);

  const fetchEmployeeImages = useCallback(async (employeeList) => {
    if (!employeeList?.length) return;
    const employeesToFetch = employeeList.filter(emp => emp.employeeId && !profileImages[emp.employeeId]);
    if (employeesToFetch.length === 0) return;

    try {
      const batchSize = 3;
      for (let i = 0; i < employeesToFetch.length; i += batchSize) {
        const batch = employeesToFetch.slice(i, i + batchSize);
        const batchPromises = batch.map(emp => fetchSingleImage(emp.employeeId));
        const batchResults = await Promise.allSettled(batchPromises);
        const successfulResults = batchResults
          .filter(result => result.status === 'fulfilled' && result.value)
          .map(result => result.value);

        if (successfulResults.length > 0) {
          setProfileImages(prev => {
            const updatedImages = { ...prev };
            successfulResults.forEach(res => {
              updatedImages[res.employeeId] = res.imageUrl;
            });
            return updatedImages;
          });
        }
      }
    } catch (err) {
      console.error('Unexpected error in fetchEmployeeImages:', err);
    }
  }, [profileImages, fetchSingleImage]);

  // --- Helper Functions ---
  const getGoalData = useCallback((officeName, positionName) => {
    const goal = goals.find(g => g.location?.name === officeName && g.position === positionName);
    return {
      scale: goal ? Number(goal.scale) : 0,
      fte: goal ? Number(goal.fte || 1) : 1
    };
  }, [goals]);

  // Get Current Count (Headcount)
  const getCurrentCount = useCallback((position) => {
    if (selectedOffice === 'All Offices') {
      return offices.reduce((sum, office) => sum + Number(employeeCountByPositionPerOffice[office.name]?.[position] || 0), 0);
    }
    if (!selectedOffice || !employeeCountByPositionPerOffice[selectedOffice]) return 0;
    return Number(employeeCountByPositionPerOffice[selectedOffice][position] || 0);
  }, [selectedOffice, offices, employeeCountByPositionPerOffice]);

  // Get Current FTE (Sum of FTEs)
  const getCurrentFTE = useCallback((position) => {
    if (selectedOffice === 'All Offices') {
      return offices.reduce((sum, office) => sum + Number(employeeFteByPositionPerOffice[office.name]?.[position] || 0), 0);
    }
    if (!selectedOffice || !employeeFteByPositionPerOffice[selectedOffice]) return 0;
    return Number(employeeFteByPositionPerOffice[selectedOffice][position] || 0);
  }, [selectedOffice, offices, employeeFteByPositionPerOffice]);

  // Get Goal Count (Target Headcount)
  const getGoalCount = useCallback((position) => {
    if (selectedOffice === 'All Offices') {
      return offices.reduce((sum, office) => sum + getGoalData(office.name, position).scale, 0);
    }
    if (!selectedOffice) return 0;
    return getGoalData(selectedOffice, position).scale;
  }, [selectedOffice, offices, getGoalData]);

  // Get Goal FTE (Target Headcount * Target FTE per person)
  const getGoalFTE = useCallback((position) => {
    if (selectedOffice === 'All Offices') {
      return offices.reduce((sum, office) => {
        const data = getGoalData(office.name, position);
        return sum + (data.scale * data.fte);
      }, 0);
    }
    if (!selectedOffice) return 0;
    const data = getGoalData(selectedOffice, position);
    return data.scale * data.fte;
  }, [selectedOffice, offices, getGoalData]);

  const getOfficePositions = useCallback(() => {
    const allPositions = new Set(positions);
    const officeList = selectedOffice === 'All Offices' ? offices : [{ name: selectedOffice }];

    officeList.forEach(office => {
      if (employeeCountByPositionPerOffice[office.name]) {
        Object.keys(employeeCountByPositionPerOffice[office.name]).forEach(pos => allPositions.add(pos));
      }
    });

    goals.forEach(goal => {
      if (goal.context === 'employees' && goal.position && (selectedOffice === 'All Offices' || goal.location?.name === selectedOffice)) {
        allPositions.add(goal.position);
      }
    });

    return Array.from(allPositions).sort();
  }, [selectedOffice, offices, employeeCountByPositionPerOffice, goals]);

  // Office Summary Calculation
  const officeSummary = useMemo(() => {
    if (!selectedOffice) return {
      totalEmployees: 0,
      totalGoals: 0,
      totalDifference: 0,
      totalEmployeesFTE: 0,
      totalGoalsFTE: 0,
      totalDifferenceFTE: 0
    };

    const officePositions = getOfficePositions();
    const totalCurrent = officePositions.reduce((sum, position) => sum + getCurrentCount(position), 0);
    const totalCurrentFTE = officePositions.reduce((sum, position) => sum + getCurrentFTE(position), 0);
    const totalGoal = officePositions.reduce((sum, position) => sum + getGoalCount(position), 0);
    const totalGoalFTE = officePositions.reduce((sum, position) => sum + getGoalFTE(position), 0);

    return {
      totalEmployees: totalCurrent,
      totalEmployeesFTE: totalCurrentFTE,
      totalGoals: totalGoal,
      totalGoalsFTE: totalGoalFTE,
      totalDifference: totalCurrent - totalGoal,
      totalDifferenceFTE: totalCurrentFTE - totalGoalFTE
    };
  }, [selectedOffice, getOfficePositions, getCurrentCount, getCurrentFTE, getGoalCount, getGoalFTE]);

  // --- FTE Validator ---
  const isValidFteMultiple = (value) => {
    if (!value || value === '') return true;
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return false;
    // Check if it's a multiple of 0.25
    return Math.abs(num * 4 - Math.round(num * 4)) < 0.001;
  };

  const formatOfficeName = (officeName) => {
    if (!officeName) return '';
    return officeName.charAt(0).toUpperCase() + officeName.slice(1);
  };

  const getLastDayOfCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  };

  const getInitials = (firstName, lastName) => {
    if (!firstName || !lastName) return 'N/A';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // --- Handlers ---
  const handleOfficeChange = (event) => {
    const newOffice = event.target.value;
    setSelectedOffice(newOffice);
    setEditingCell(null);
    const params = new URLSearchParams();
    if (newOffice !== 'All Offices') {
      params.set('office', newOffice);
    } else {
      params.delete('office');
    }
    navigate(`?${params.toString()}`);
  };

  const handleEditClick = (position) => {
    if (selectedOffice === 'All Offices') return;
    setEditingCell(position);
    setEditValue(getGoalCount(position).toString());
    const goal = goals.find(g => g.location?.name === selectedOffice && g.position === position && g.context === 'employees');
    setEditFteValue(goal ? (goal.fte || 1).toString() : '1');
    setEditFteMode('perPerson'); // Default to per person mode
  };

  const handleEditFteChange = (e) => {
    const value = e.target.value;

    if (editFteMode === 'total') {
      // Allow empty, or valid decimal that's a multiple of 0.25
      if (value === '' || (/^\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
        setEditFteValue(value);
      }
    } else {
      // Per Person mode - just set the value
      setEditFteValue(value);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingCell || !selectedOffice || selectedOffice === 'All Offices') return;

    const newGoal = parseInt(editValue);
    const fteInput = parseFloat(editFteValue);

    if (isNaN(newGoal) || newGoal < 0 || newGoal > 1000) {
      setError('Scale must be a number between 0 and 1000');
      return;
    }

    if (isNaN(fteInput) || fteInput < 0) {
      setError('FTE must be a valid number');
      return;
    }

    // Validate Total FTE mode
    if (editFteMode === 'total' && !isValidFteMultiple(editFteValue)) {
      setError('Total FTE must be a multiple of 0.25 (e.g., 5, 5.25, 5.5, 5.75)');
      return;
    }

    try {
      const selectedOfficeObj = offices.find(office => office.name === selectedOffice);
      if (!selectedOfficeObj) {
        setError('Selected office not found');
        return;
      }

      // Calculate final FTE and scale based on mode
      let finalScale, finalFte;

      if (editFteMode === 'total') {
        // Total FTE mode: User inputs total directly
        // We need to store both scale and fte_per_person
        // Strategy: Store scale as the goal count, and calculate fte_per_person
        finalScale = newGoal;
        finalFte = fteInput / (newGoal || 1); // fte_per_person = total_fte / scale
      } else {
        // Per Person mode: Multiply scale × fte
        finalScale = newGoal;
        finalFte = fteInput;
      }

      const goalData = {
        goal: newGoal === 0
          ? `No hiring needed for ${editingCell} in ${selectedOffice}`
          : `Hire ${newGoal} ${editingCell} for ${selectedOffice}`,
        context: 'employees',
        position: editingCell,
        location: { id: selectedOfficeObj.id, name: selectedOffice },
        scale: finalScale,
        fte: finalFte,
        date: `${getLastDayOfCurrentMonth()}T23:59:59`,
        description: `Position: ${editingCell}, Goal: ${newGoal}, FTE Mode: ${editFteMode}, FTE: ${finalFte}`,
        timestamp: new Date().toISOString()
      };

      let updatedGoals = [...goals];
      const existingGoal = goals.find(g =>
        g.location?.name === selectedOffice &&
        g.position === editingCell &&
        g.context === 'employees'
      );

      if (existingGoal) {
        await updateGoal(existingGoal.id, { ...goalData, id: existingGoal.id });
        setSuccess(`Updated goal for ${editingCell}`);
        updatedGoals = updatedGoals.map(g => g.id === existingGoal.id ? { ...goalData, id: existingGoal.id } : g);
      } else {
        const response = await createGoal(goalData);
        setSuccess(`Created new goal for ${editingCell}`);
        updatedGoals = [...updatedGoals, { ...goalData, id: response.id || Date.now() }];
      }

      setGoals(updatedGoals);

      // ✅ Create/Update Total Employee Goal (for EmployeeCard chart)
      await createOrUpdateTotalEmployeeGoal(selectedOffice, updatedGoals);

      setEditingCell(null);
      setEditValue('');
      setEditFteValue('');
      setEditFteMode('perPerson');
    } catch (error) {
      console.error('Failed to save goal:', error);
      setError('Failed to save goal. Please check your input and try again.');
    }
  };

  // ✅ Create/Update Total Employee Goal (without position field)
  const createOrUpdateTotalEmployeeGoal = async (officeName, currentGoals) => {
    try {
      const officeObj = offices.find(office => office.name === officeName);
      if (!officeObj) return;

      // Calculate total goal FTE for this office
      const officePositions = getOfficePositions();
      const totalGoalFTE = officePositions.reduce((sum, position) => sum + getGoalFTE(position), 0);

      // Find existing total goal (without position)
      const existingTotalGoal = currentGoals.find(g =>
        g.context === 'employees' &&
        !g.position && // No position field
        g.location?.name === officeName
      );

      const totalGoalData = {
        goal: `Total employee goal for ${officeName}`,
        context: 'employees',
        // No position field - this is the key!
        location: { id: officeObj.id, name: officeName },
        scale: Math.round(totalGoalFTE), // Round to nearest integer for headcount
        fte: totalGoalFTE, // Store actual FTE total
        date: `${getLastDayOfCurrentMonth()}T23:59:59`,
        description: `Total employee goal (FTE: ${totalGoalFTE.toFixed(2)})`,
        timestamp: new Date().toISOString()
      };

      if (existingTotalGoal) {
        await updateGoal(existingTotalGoal.id, { ...totalGoalData, id: existingTotalGoal.id });
      } else {
        await createGoal(totalGoalData);
      }

      // Reload goals to get the updated total goal
      const updatedGoals = await getGoals();
      setGoals(updatedGoals.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
    } catch (error) {
      console.error('Failed to create/update total employee goal:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
    setEditFteValue('');
    setEditFteMode('perPerson');
  };

  const handleOpenDialog = (goal = null) => {
    if (goal) {
      setEditingGoal(goal);
      setFormData({
        goal: goal.goal,
        date: goal.date ? goal.date.split('T')[0] : '',
        context: goal.context,
        position: goal.position || '',
        location: goal.location?.id || '',
        description: goal.description || '',
        scale: goal.scale,
        fte: goal.fte || 1
      });
    } else {
      setEditingGoal(null);
      setFormData({
        goal: '',
        date: '',
        context: '',
        position: '',
        location: selectedOffice && selectedOffice !== 'All Offices' ?
          offices.find(office => office.name === selectedOffice)?.id || '' : '',
        description: '',
        scale: 1,
        fte: 1
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGoal(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.goal || !formData.date || !formData.context || !formData.location || formData.scale < 0) {
      setError('Please fill all required fields correctly.');
      return;
    }

    try {
      const selectedOfficeForForm = offices.find(office => office.id === formData.location);
      if (!selectedOfficeForForm) {
        setError('Selected office not found');
        return;
      }

      const payload = {
        ...formData,
        location: { id: selectedOfficeForForm.id, name: selectedOfficeForForm.name },
        timestamp: new Date().toISOString(),
        date: formData.date
      };

      if (editingGoal) {
        await updateGoal(editingGoal.id, payload);
        setSuccess('Goal updated successfully!');
      } else {
        await createGoal(payload);
        setSuccess('Goal created successfully!');
      }

      handleCloseDialog();
      loadData();

      // ✅ Update total employee goal if this is an employee goal
      if (payload.context === 'employees' && payload.position) {
        await createOrUpdateTotalEmployeeGoal(selectedOfficeForForm.name, goals);
      }
    } catch (error) {
      setError('Failed to save goal. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditValueChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
      setEditValue(value);
    }
  };

  // --- Overlay & Search ---
  const handleCurrentCountClick = async (position) => {
    if (selectedOffice === 'All Offices') return;
    setEmployeeOverlay({ open: true, position, employees: [], loading: true, searchTerm: '' });

    try {
      const allEmployees = await getAllEmployees();
      const filteredEmployees = allEmployees.filter(employee => {
        const employeeOffice = employee.office?.name || employee.office;
        const hasPosition = (employee.positions && employee.positions.some(p => p.positionName === position)) ||
          employee.position === position;
        return employeeOffice === selectedOffice && hasPosition;
      });

      const formattedEmployees = filteredEmployees.map(emp => {
        const posData = emp.positions?.find(p => p.positionName === position);
        const specificFte = posData ? posData.fte : 1.0;
        return {
          id: emp.employeeId || emp.id,
          firstName: emp.firstName || 'Unknown',
          lastName: emp.lastName || 'Unknown',
          position: emp.position || position,
          fte: specificFte,
          email: emp.user?.email || emp.email || '',
          office: emp.office?.name || emp.office || selectedOffice,
          employeeId: emp.employeeId || emp.id
        }
      });

      setEmployeeOverlay(prev => ({ ...prev, employees: formattedEmployees, loading: false }));
      if (formattedEmployees.length > 0) fetchEmployeeImages(formattedEmployees);
    } catch (error) {
      setError('Failed to load employees.');
      setEmployeeOverlay(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCloseEmployeeOverlay = () => {
    setEmployeeOverlay({ open: false, position: '', employees: [], loading: false, searchTerm: '' });
  };

  const handleEmployeeSearch = (event) => {
    setEmployeeOverlay(prev => ({ ...prev, searchTerm: event.target.value }));
  };

  const getFilteredEmployees = () => {
    if (!employeeOverlay.searchTerm) return employeeOverlay.employees;
    const searchTerm = employeeOverlay.searchTerm.toLowerCase();
    return employeeOverlay.employees.filter(employee =>
      employee.firstName.toLowerCase().includes(searchTerm) ||
      employee.lastName.toLowerCase().includes(searchTerm)
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: colors.primary2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3, backgroundColor: 'transparent', minHeight: '100vh' }}>
      <PageHeader
        title={selectedOffice ? `Goals Management - ${formatOfficeName(selectedOffice)}` : 'Goals Management'}
        subtitle="Manage headcount and FTE goals per office"
        icon={<BusinessIcon sx={{ color: 'primary.main' }} />}
      />

      <Card sx={{ mb: 3, borderRadius: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Select Office</InputLabel>
                <Select
                  value={selectedOffice}
                  onChange={handleOfficeChange}
                  label="Select Office"
                  sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 } }}
                >
                  <MenuItem value="All Offices">
                    <BusinessIcon sx={{ mr: 1, color: colors.primary2 }} />
                    All Offices
                  </MenuItem>
                  {offices.map((office) => (
                    <MenuItem key={office.id} value={office.name}>
                      <BusinessIcon sx={{ mr: 1, color: colors.primary2 }} />
                      {office.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: 2 }}>
                <Box textAlign="center">
                  <Typography variant="h5" sx={{ color: colors.primary1, fontWeight: 'bold' }}>
                    {officeSummary.totalEmployees}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Current</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>
                    (FTE: {Number(officeSummary.totalEmployeesFTE).toFixed(2)})
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h5" sx={{ color: colors.success, fontWeight: 'bold' }}>
                    {officeSummary.totalGoals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Goal Headcount</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>
                    (Goal FTE: {Number(officeSummary.totalGoalsFTE).toFixed(2)})
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h5" sx={{
                    color: officeSummary.totalDifference > 0 ? colors.success :
                      officeSummary.totalDifference < 0 ? colors.danger : colors.info,
                    fontWeight: 'bold'
                  }}>
                    {officeSummary.totalDifference > 0 ? '+' : ''}{officeSummary.totalDifference}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Diff (Headcount)</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: officeSummary.totalDifferenceFTE < 0 ? colors.danger : 'text.disabled' }}>
                    (FTE: {officeSummary.totalDifferenceFTE > 0 ? '+' : ''}{Number(officeSummary.totalDifferenceFTE).toFixed(2)})
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedOffice && (
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2} justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <PeopleIcon sx={{ mr: 1, color: colors.primary1 }} />
              <Typography variant="h5" sx={{ color: colors.primary1, fontWeight: 'bold' }}>
                Office - {formatOfficeName(selectedOffice)} Goals
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ backgroundColor: colors.success, '&:hover': { backgroundColor: '#388e3c' } }}
            >
              Add Goal
            </Button>
          </Box>

          <StyledTableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Position</TableCell>
                  <TableCell align="center">Current (Count)</TableCell>
                  <TableCell align="center">Current FTE</TableCell>
                  <TableCell align="center">Goal (Count)</TableCell>
                  <TableCell align="center">Goal FTE (Total)</TableCell>
                  <TableCell align="center">Diff (Headcount)</TableCell>
                  <TableCell align="center">Diff (FTE)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getOfficePositions().map((position) => {
                  const current = getCurrentCount(position);
                  const currentFTE = getCurrentFTE(position);
                  const goalCount = getGoalCount(position);
                  const goalFTE = getGoalFTE(position);
                  const diffCount = current - goalCount;
                  const diffFTE = currentFTE - goalFTE;
                  const isEditing = editingCell === position;
                  const isAllOffices = selectedOffice === 'All Offices';

                  return (
                    <TableRow key={position}>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                        {position}
                      </TableCell>
                      <ClickableTableCell
                        align="center"
                        sx={{ fontWeight: 'bold', color: colors.primary1 }}
                        onClick={() => handleCurrentCountClick(position)}
                      >
                        {current}
                        <ExpandMoreIcon sx={{ ml: 1, fontSize: 16, verticalAlign: 'middle' }} />
                      </ClickableTableCell>
                      <TableCell align="center" sx={{ color: colors.primary3 }}>
                        {Number(currentFTE).toFixed(2)}
                      </TableCell>
                      <EditableTableCell
                        align="center"
                        isEditing={isEditing}
                        onClick={() => !isEditing && !isAllOffices && handleEditClick(position)}
                      >
                        {isEditing ? (
                          <Box display="flex" flexDirection="column" gap={1} alignItems="center">
                            {/* Scale Input */}
                            <Box display="flex" alignItems="center" gap={1}>
                              <TextField
                                size="small"
                                type="number"
                                value={editValue}
                                onChange={handleEditValueChange}
                                inputProps={{ min: 0, max: 1000, style: { textAlign: 'center', width: '60px' } }}
                              />
                            </Box>

                            {/* ✅ FTE Mode Selector */}
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <RadioGroup
                                row
                                value={editFteMode}
                                onChange={(e) => setEditFteMode(e.target.value)}
                                sx={{ gap: 1 }}
                              >
                                <FormControlLabel
                                  value="perPerson"
                                  control={<Radio size="small" />}
                                  label={<Typography variant="caption">Per Person</Typography>}
                                />
                                <FormControlLabel
                                  value="total"
                                  control={<Radio size="small" />}
                                  label={<Typography variant="caption">Total</Typography>}
                                />
                              </RadioGroup>
                            </Box>

                            {/* Per Person FTE Dropdown */}
                            {editFteMode === 'perPerson' && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption" sx={{ minWidth: '80px' }}>FTE:</Typography>
                                <Select
                                  size="small"
                                  value={editFteValue}
                                  onChange={handleEditFteChange}
                                  sx={{ width: '100px', fontSize: '0.8rem' }}
                                >
                                  <MenuItem value="1">1.0</MenuItem>
                                  <MenuItem value="0.75">0.75</MenuItem>
                                  <MenuItem value="0.5">0.5</MenuItem>
                                  <MenuItem value="0.25">0.25</MenuItem>
                                </Select>
                              </Box>
                            )}

                            {/* Total FTE Text Input */}
                            {editFteMode === 'total' && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption" sx={{ minWidth: '80px' }}>Total FTE:</Typography>
                                <TextField
                                  size="small"
                                  type="text"
                                  value={editFteValue}
                                  onChange={handleEditFteChange}
                                  inputProps={{
                                    style: { textAlign: 'center', width: '80px' },
                                    placeholder: 'e.g., 5.5'
                                  }}
                                  sx={{ width: '120px' }}
                                  error={!!editFteValue && !isValidFteMultiple(editFteValue)}
                                  helperText={editFteValue && !isValidFteMultiple(editFteValue) ? 'Must be multiple of 0.25' : ''}
                                />
                              </Box>
                            )}

                            {/* Save/Cancel Buttons */}
                            <Box display="flex" gap={1}>
                              <IconButton size="small" onClick={handleSaveEdit} sx={{ color: colors.success }}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={handleCancelEdit} sx={{ color: colors.danger }}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <Typography sx={{ fontWeight: 'bold', color: colors.success }}>
                              {goalCount}
                            </Typography>
                            {!isAllOffices && <EditIcon fontSize="small" sx={{ color: colors.primary3, opacity: 0.7 }} />}
                          </Box>
                        )}
                      </EditableTableCell>
                      <TableCell align="center" sx={{ color: colors.success, opacity: 0.8 }}>
                        {Number(goalFTE).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={diffCount > 0 ? `+${diffCount}` : diffCount}
                          sx={{
                            backgroundColor: diffCount > 0 ? colors.success :
                              diffCount < 0 ? colors.danger : colors.info,
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{
                          color: diffFTE > 0 ? colors.success : diffFTE < 0 ? colors.danger : colors.info,
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}>
                          {diffFTE > 0 ? '+' : ''}{Number(diffFTE).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Total Row */}
                <TableRow sx={{ backgroundColor: colors.primary4, '& td': { fontWeight: 'bold' } }}>
                  <TableCell sx={{ color: colors.primary1, fontSize: '1.1rem' }}>Total</TableCell>
                  <TableCell align="center" sx={{ color: colors.primary1, fontSize: '1.1rem' }}>{officeSummary.totalEmployees}</TableCell>
                  <TableCell align="center" sx={{ color: colors.primary1 }}>{Number(officeSummary.totalEmployeesFTE).toFixed(2)}</TableCell>
                  <TableCell align="center" sx={{ color: colors.success, fontSize: '1.1rem' }}>{officeSummary.totalGoals}</TableCell>
                  <TableCell align="center" sx={{ color: colors.success }}>{Number(officeSummary.totalGoalsFTE).toFixed(2)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={officeSummary.totalDifference > 0 ? `+${officeSummary.totalDifference}` : officeSummary.totalDifference}
                      sx={{
                        backgroundColor: officeSummary.totalDifference > 0 ? colors.success :
                          officeSummary.totalDifference < 0 ? colors.danger : colors.info,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontWeight: 'bold', color: officeSummary.totalDifferenceFTE < 0 ? 'white' : 'inherit' }}>
                      {officeSummary.totalDifferenceFTE > 0 ? '+' : ''}{Number(officeSummary.totalDifferenceFTE).toFixed(2)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Box>
      )}

      {/* Dialog for Add/Edit Goal */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: colors.primary1, color: 'white' }}>
          {editingGoal ? 'Edit Goal' : 'Add New Goal'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus margin="dense" name="goal" label="Goal Title" type="text"
              fullWidth variant="outlined" value={formData.goal} onChange={handleInputChange}
              required sx={{ mb: 2 }}
            />
            <TextField
              margin="dense" name="context" label="Context" select
              fullWidth variant="outlined" value={formData.context} onChange={handleInputChange}
              required sx={{ mb: 2 }}
            >
              <MenuItem value="employees">Employees</MenuItem>
              <MenuItem value="candidates">Candidates</MenuItem>
              <MenuItem value="job">Jobs</MenuItem>
              <MenuItem value="leave">Leaves</MenuItem>
            </TextField>
            <TextField
              margin="dense" name="position" label="Position" select
              fullWidth variant="outlined" value={formData.position} onChange={handleInputChange}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">None</MenuItem>
              {positions.map((position) => (
                <MenuItem key={position} value={position}>{position}</MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense" name="location" label="Location" select
              fullWidth variant="outlined" value={formData.location} onChange={handleInputChange}
              required sx={{ mb: 2 }}
            >
              {offices.map((office) => (
                <MenuItem key={office.id} value={office.id}>{office.name}</MenuItem>
              ))}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  margin="dense" name="scale" label="Scale (Count)" type="number"
                  fullWidth variant="outlined" inputProps={{ min: 0, max: 1000 }}
                  value={formData.scale} onChange={handleInputChange}
                  required sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  margin="dense" name="fte" label="FTE per Person" select
                  fullWidth variant="outlined" value={formData.fte} onChange={handleInputChange}
                  required sx={{ mb: 2 }}
                >
                  <MenuItem value={1}>1.0 (Full Time)</MenuItem>
                  <MenuItem value={0.75}>0.75 (3/4 Time)</MenuItem>
                  <MenuItem value={0.5}>0.5 (Half Time)</MenuItem>
                  <MenuItem value={0.25}>0.25 (Quarter Time)</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <TextField
              margin="dense" name="date" label="Target Date" type="date"
              fullWidth variant="outlined" InputLabelProps={{ shrink: true }}
              value={formData.date} onChange={handleInputChange}
              required sx={{ mb: 2 }}
            />
            <TextField
              margin="dense" name="description" label="Description" multiline rows={3}
              fullWidth variant="outlined" value={formData.description} onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog} sx={{ color: colors.primary3 }}>Cancel</Button>
            <ActionButton type="submit">{editingGoal ? 'Update' : 'Create'}</ActionButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Employee Overlay */}
      {employeeOverlay.open && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1300 }} onClick={handleCloseEmployeeOverlay}>
          <Paper elevation={4} sx={{ width: '90%', maxWidth: '600px', backgroundColor: 'background.paper', borderRadius: '16px', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <Box sx={{ padding: '16px', backgroundColor: colors.primary1, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ margin: 0 }}>Employees - {employeeOverlay.position}</Typography>
              <IconButton onClick={handleCloseEmployeeOverlay} sx={{ color: 'white' }}><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ padding: '16px', borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: 'background.default', borderRadius: '8px', padding: '0 12px' }}>
                <SearchIcon sx={{ color: 'text.secondary' }} />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={employeeOverlay.searchTerm}
                  onChange={handleEmployeeSearch}
                  style={{ border: 'none', background: 'transparent', padding: '12px', width: '100%', outline: 'none', color: theme.palette.text.primary }}
                />
              </Box>
            </Box>
            <Box sx={{ padding: '16px', maxHeight: '60vh', overflowY: 'auto' }}>
              {employeeOverlay.loading ? <Box display="flex" justifyContent="center"><CircularProgress /></Box> :
                getFilteredEmployees().map((emp, i) => (
                  <Box key={i} sx={{ padding: '10px', borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: colors.primary2, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px', overflow: 'hidden' }}>
                      {profileImages[emp.employeeId] ? <img src={profileImages[emp.employeeId]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getInitials(emp.firstName, emp.lastName)}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">{emp.firstName} {emp.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{emp.email} <br /> <WorkIcon fontSize="inherit" style={{ verticalAlign: 'middle' }} /> {emp.position} (FTE: {emp.fte})</Typography>
                    </Box>
                  </Box>
                ))
              }
              {!employeeOverlay.loading && getFilteredEmployees().length === 0 && (
                <Typography align="center" color="text.secondary">No employees found.</Typography>
              )}
            </Box>
          </Paper>
        </div>
      )}

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GoalManagement;